// Mutation cache-strategy taxonomy
//
// Every mutation in this file uses one or more of the strategies below
// (strategies may compose, e.g. plain-invalidate + cross-entity-invalidate).
//
// 1. Optimistic update (onMutate + onError rollback) followed by onSuccess
//    setQueryData: for update mutations needing immediate UI feedback without a
//    round-trip. Used by useUpdateConnectionMutation, whose onSuccess composes
//    setQueryData (the single-connection cache) AND setQueriesData with a
//    partial-key match (every connection-list cache, regardless of type filter)
//    so both views reflect the server response without a refetch.
// 2. Plain invalidate (onSuccess invalidateQueries only): for any mutation
//    where the server is the source of truth (create, delete, rotate, test).
//    Used by useCreateConnectionMutation, useDeleteConnectionMutation,
//    useRotateConnectionKeyMutation, and useTestConnectionMutation.
// 3. Cross-entity invalidation: any mutation that affects another entity's
//    count (connections affecting topics) adds a topics invalidation. Used by
//    useCreateConnectionMutation and useDeleteConnectionMutation, which compose
//    a plain connections-list invalidate with a topics-list invalidate.

// 1. React and DOM imports

// 2. External libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';

// 3. Internal components

// 4. Type imports
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  ConnectionResponse,
  CreateConnectionRequest,
  CreateConnectionResponse,
  RotateConnectionKeyResponse,
  TestConnectionResponse,
  UpdateConnectionRequest,
} from '~/generated/api/model';

// 5. Custom hooks and utilities
import {
  createConnectionV1TopicsTopicIdConnectionsPost,
  deleteConnectionV1TopicsTopicIdConnectionsConnectionIdDelete,
  rotateConnectionKeyV1TopicsTopicIdConnectionsConnectionIdRotateKeyPost,
  testConnectionV1TopicsTopicIdConnectionsConnectionIdTestPost,
  updateConnectionV1TopicsTopicIdConnectionsConnectionIdPatch,
} from '~/generated/api/connections/connections';
import { queryKeys } from './queryKeys';

export function useCreateConnectionMutation(
  topicId: string
): UseMutationResult<CreateConnectionResponse, Error, CreateConnectionRequest, unknown> {
  const queryClient = useQueryClient();

  return useMutation<CreateConnectionResponse, Error, CreateConnectionRequest, unknown>({
    mutationFn: async (data: CreateConnectionRequest) => {
      return createConnectionV1TopicsTopicIdConnectionsPost(topicId, data);
    },
    onSuccess: () => {
      // Invalidate connections list to refetch with new connection.
      // Prefix key matches every type-filtered list variant, so exact: false.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.connections.list(topicId),
        exact: false,
      });
      // Invalidate topics to refresh connection counts
      void queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
      // Invalidate single-topic cache so TopicDetailPage's connection count stays in sync
      void queryClient.invalidateQueries({ queryKey: queryKeys.topic(topicId) });
    },
  });
}

interface UpdateConnectionContext {
  previousConnection: ConnectionResponse | null;
}

export function useUpdateConnectionMutation(
  topicId: string,
  connectionId: string
): UseMutationResult<ConnectionResponse, Error, UpdateConnectionRequest, UpdateConnectionContext> {
  const queryClient = useQueryClient();

  return useMutation<ConnectionResponse, Error, UpdateConnectionRequest, UpdateConnectionContext>({
    mutationFn: async (data: UpdateConnectionRequest) => {
      return updateConnectionV1TopicsTopicIdConnectionsConnectionIdPatch(
        topicId,
        connectionId,
        data
      );
    },
    // Optimistic update: immediately update cache before API responds
    onMutate: async (updatedData) => {
      // Cancel outgoing refetches to prevent race conditions
      // Use partial match to catch all connection list queries regardless of type filter
      await queryClient.cancelQueries({
        queryKey: queryKeys.connections.list(topicId),
        exact: false,
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.connection(topicId, connectionId),
      });

      // Get previous connection from any matching query cache
      const queries = queryClient.getQueriesData<ConnectionResponse[]>({
        queryKey: queryKeys.connections.list(topicId),
        exact: false,
      });
      const previousConnection =
        queries.flatMap(([, data]) => data ?? []).find((c) => c.id === connectionId) ?? null;

      // Optimistically update all connection list caches (handles type filter variants)
      queryClient.setQueriesData<ConnectionResponse[]>(
        { queryKey: queryKeys.connections.list(topicId), exact: false },
        (old) => {
          if (!old) return old;
          return old.map((connection) => {
            if (connection.id !== connectionId) return connection;
            return {
              ...connection,
              status: updatedData.status ?? connection.status,
              configSummary: {
                ...(connection.configSummary as Record<string, unknown>),
                ...(updatedData.config?.canRead !== undefined && {
                  canRead: updatedData.config.canRead,
                }),
                ...(updatedData.config?.canWrite !== undefined && {
                  canWrite: updatedData.config.canWrite,
                }),
              },
            };
          });
        }
      );

      return { previousConnection };
    },
    // Rollback on error
    onError: (_err, _updatedData, context) => {
      if (context?.previousConnection) {
        queryClient.setQueriesData<ConnectionResponse[]>(
          { queryKey: queryKeys.connections.list(topicId), exact: false },
          (old) => {
            if (!old) return old;
            return old.map((c) => (c.id === connectionId ? context.previousConnection! : c));
          }
        );
      }
    },
    // Update cache with server response (no refetch needed)
    onSuccess: (updatedConnection) => {
      // Update single connection cache with server response
      queryClient.setQueryData(queryKeys.connection(topicId, connectionId), updatedConnection);

      // Update all connection list caches with server response (handles type filter variants)
      queryClient.setQueriesData<ConnectionResponse[]>(
        { queryKey: queryKeys.connections.list(topicId), exact: false },
        (old) => {
          if (!old) return old;
          return old.map((c) => (c.id === connectionId ? updatedConnection : c));
        }
      );
    },
  });
}

export function useDeleteConnectionMutation(
  topicId: string
): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string, unknown>({
    mutationFn: async (connectionId: string) => {
      return deleteConnectionV1TopicsTopicIdConnectionsConnectionIdDelete(topicId, connectionId);
    },
    onSuccess: () => {
      // Invalidate connections list to refetch without deleted connection.
      // Prefix key matches every type-filtered list variant, so exact: false.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.connections.list(topicId),
        exact: false,
      });
      // Invalidate topics to refresh connection counts
      void queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
      // Invalidate single-topic cache so TopicDetailPage's connection count stays in sync
      void queryClient.invalidateQueries({ queryKey: queryKeys.topic(topicId) });
    },
  });
}

export function useRotateConnectionKeyMutation(
  topicId: string,
  connectionId: string
): UseMutationResult<RotateConnectionKeyResponse, Error, void, unknown> {
  const queryClient = useQueryClient();

  return useMutation<RotateConnectionKeyResponse, Error, void, unknown>({
    mutationFn: async () => {
      return rotateConnectionKeyV1TopicsTopicIdConnectionsConnectionIdRotateKeyPost(
        topicId,
        connectionId
      );
    },
    onSuccess: () => {
      // Invalidate single connection to refetch with new API key
      void queryClient.invalidateQueries({ queryKey: queryKeys.connection(topicId, connectionId) });
    },
  });
}

export function useTestConnectionMutation(
  topicId: string,
  connectionId: string
): UseMutationResult<TestConnectionResponse, Error, void, unknown> {
  const queryClient = useQueryClient();

  return useMutation<TestConnectionResponse, Error, void, unknown>({
    mutationFn: async () => {
      return testConnectionV1TopicsTopicIdConnectionsConnectionIdTestPost(topicId, connectionId);
    },
    onSuccess: () => {
      // Invalidate connection to refetch updated state
      void queryClient.invalidateQueries({ queryKey: queryKeys.connection(topicId, connectionId) });
    },
  });
}
